import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, MenuItem, Stack, Typography } from '@mui/material';
import Select from '../forms/fields/select';
import { useFormContext } from 'react-hook-form';
import { getVenueById } from '../../../features/venue/venueSlice';

const GreenSelector = ({ game, defaults }) => {
  const dispatch = useDispatch();
  const {
    control,
    watch,
    formState: { errors }
  } = useFormContext();

  const event = useSelector((state) => state.event.event);
  const venue = useSelector((state) => state.venue.venue);
  const loadingVenue = useSelector((state) => state.venue.loadingVenue);

  // Determine if multi-venue
  const isMultiVenue = event?.venue?.id === 'multiple' && Array.isArray(event?.locations);
  // Watch selected location for multi-venue
  const selectedLocationId = watch('venue');
  const selectedGreenName = watch('green');

  // Get greens for selected location (multi-venue) or for venue (single venue)
  const getAvailableGreens = () => {
    if (isMultiVenue) {
      const selectedLocation = event.locations.find((loc) => loc.id === selectedLocationId);
      if (!selectedLocation) return [];
      return (selectedLocation.greens || []).map((green) => ({
        name: green.name,
        rink_count: green.rink_count || 0,
        locationId: selectedLocation.id
      }));
    } else if (venue?.greens) {
      return venue.greens.map((green) => ({
        name: green.name,
        rink_count: green.rink_count || 0,
        locationId: venue.id
      }));
    }
    return [];
  };

  useEffect(() => {
    const gameVenueId = game?.venue?.id || game?.venue;

    if (gameVenueId) {
      dispatch(getVenueById(gameVenueId));
    } else if (event && event.venue && event.venue.id !== 'multiple') {
      dispatch(getVenueById(event.venue.id));
    }
  }, [game, dispatch, event]);

  const greens = getAvailableGreens();
  const selectedGreen = greens.find((g) => g.name === selectedGreenName) || null;

  // defaults prefill a NEW game, e.g. from a bracket match's draw-sheet
  // location; game is the saved game being edited.
  const defaultLocationId = defaults?.venue ?? game?.venue?.id ?? game?.venue ?? '';
  const defaultGreenName = defaults?.green ?? game?.green ?? '';
  const defaultRink = defaults?.rink ?? game?.rink ?? '';

  return loadingVenue ? (
    <Box display={'flex'} gap={2} my={2}>
      <CircularProgress />
    </Box>
  ) : (
    (venue || isMultiVenue) && (
      <Stack my={2}>
        {isMultiVenue ? (
          <Select
            control={control}
            name="venue"
            label="Location"
            defaultValue={defaultLocationId}
            error={errors?.location}
            helperText={errors?.location?.message}
            sx={{ minWidth: 200, mr: 2 }}
            disabled={game?.venue}>
            <MenuItem value="">Select a location</MenuItem>
            {event.locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.name}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Typography variant="subtitle1">Venue: {venue?.name}</Typography>
        )}

        <Box display={'flex'} gap={2}>
          <Select
            control={control}
            name={'green'}
            defaultValue={defaultGreenName}
            label="Green"
            error={errors?.green}
            helperText={errors?.green?.message}>
            <MenuItem key={'na'} value={'na'}>
              TBA/NA
            </MenuItem>
            {greens.map((green) => (
              <MenuItem key={green.name} value={green.name}>
                {green.name}
              </MenuItem>
            ))}
          </Select>

          {selectedGreen && (
            <Select
              control={control}
              name={'rink'}
              defaultValue={defaultRink}
              label="Rink"
              error={errors?.rink}
              helperText={errors?.rink?.message}>
              {[...Array(selectedGreen.rink_count || 0)].map((_, idx) => (
                <MenuItem key={idx + 1} value={idx + 1}>
                  {idx + 1}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Stack>
    )
  );
};

export default GreenSelector;
